import { formatHeading, formatBulletList } from './utils';

/**
 * Format Marketing Strategist (Landing Page or Blog Post) data for copying as Markdown
 * Outputs clean markdown that matches what's displayed in the renderer
 */
export const formatMarketingStrategistCopy = (data: any): string => {
  if (!data || (!data.hero && !data.sections && !data.site_architecture)) {
    return 'No marketing strategist content available';
  }

  const pageType = data.page_type || 'general';
  const isBlogPost = pageType === 'blog_post';
  const isPositioningDoc = pageType === 'positioning_document';
  const isWebsiteStructure = pageType === 'website_structure';
  const isLandingPage = pageType === 'landing_page';

  // Positioning Document Formatting
  if (isPositioningDoc) {
    const sections: string[] = [];

    // Title
    if (data.hero?.h1) {
      sections.push(formatHeading(data.hero.h1, 1));
    }

    // Strategic Content
    if (data.sections && data.sections.length > 0) {
      data.sections.forEach((section: any) => {
        if (section.heading) {
          sections.push(formatHeading(section.heading, 2));
        }

        if (section.subsections && section.subsections.length > 0) {
          section.subsections.forEach((subsection: any) => {
            if (subsection.subheading) {
              sections.push(formatHeading(subsection.subheading, 3));
            }
            if (subsection.content) {
              sections.push(subsection.content);
            }
          });
        }
      });
    }

    return sections.join('\n\n');
  }

  // Website Structure Formatting
  if (isWebsiteStructure && data.site_architecture) {
    const sections: string[] = [];

    // Title
    if (data.hero?.h1) {
      sections.push(formatHeading(data.hero.h1, 1));
    }

    // Phase 1: Positioning Inputs
    if (data.site_architecture.positioning_inputs) {
      sections.push(formatHeading('Phase 1: Positioning Inputs', 2));
      const pi = data.site_architecture.positioning_inputs;
      sections.push(`**Primary ICP:** ${pi.primary_icp || 'N/A'}`);
      if (pi.secondary_icps?.length > 0) {
        sections.push(`**Secondary ICPs:** ${pi.secondary_icps.join(', ')}`);
      }
      sections.push(`**Job to be Done:** ${pi.job_to_be_done || 'N/A'}`);
      sections.push(`**Core Pain:** ${pi.core_pain || 'N/A'}`);
      sections.push(`**Category to Own:** ${pi.category_to_own || 'N/A'}`);
      if (pi.alternatives?.length > 0) {
        sections.push(`**Alternatives:** ${pi.alternatives.join(', ')}`);
      }
      sections.push(`**Value Proposition:** ${pi.value_proposition || 'N/A'}`);
      sections.push(`**Disqualifier:** ${pi.disqualifier || 'N/A'}`);
    }

    // Phase 2: Keyword Clusters
    if (data.site_architecture.keyword_clusters?.length > 0) {
      sections.push(formatHeading('Phase 2: Keyword Clusters', 2));
      sections.push('| Cluster | Intent | Funnel | ICP | Page |');
      sections.push('|---------|--------|--------|-----|------|');
      data.site_architecture.keyword_clusters.forEach((cluster: any) => {
        sections.push(
          `| ${cluster.cluster_name} | ${cluster.intent_type} | ${cluster.funnel_stage?.replace('_', ' ')} | ${cluster.icp_relevance} | \`${cluster.assigned_page}\` |`,
        );
      });
    }

    // Phase 3-4: Page Types & URL Hierarchy
    if (data.site_architecture.pages && data.site_architecture.pages.length > 0) {
      sections.push(formatHeading('Phase 3-4: Page Types & URL Hierarchy', 2));

      data.site_architecture.pages.forEach((page: any) => {
        sections.push(formatHeading(page.page_name, 3));
        sections.push(`\`${page.url_slug}\``);
        const metadata = [];
        if (page.page_type) metadata.push(`Type: ${page.page_type}`);
        if (page.parent_page) metadata.push(`Parent: \`${page.parent_page}\``);
        if (metadata.length > 0) sections.push(metadata.join(' | '));
        sections.push('');
        sections.push(`*${page.page_purpose}*`);

        {
          // Backwards compatible: support both old (nested content_hierarchy) and new (flat) formats
          const pageH1 = page.h1 || page.content_hierarchy?.h1;
          const isOldFormat = page.content_hierarchy && !page.h1;
          const h2Sections = isOldFormat ? page.content_hierarchy.h2_sections : page.h2_sections;

          if (pageH1 || h2Sections) {
            sections.push('');
            sections.push('**Content Hierarchy**');
            sections.push('');
            if (pageH1) sections.push(`**H1:** ${pageH1}`);

            if (h2Sections && h2Sections.length > 0) {
              h2Sections.forEach((h2Item: any) => {
                const isObject = typeof h2Item === 'object';
                sections.push('');
                sections.push(`**H2:** ${isObject ? h2Item.h2 : h2Item}`);

                if (isObject && h2Item.h3_subsections && h2Item.h3_subsections.length > 0) {
                  h2Item.h3_subsections.forEach((h3val: string) => {
                    sections.push(`**H3:** ${h3val}`);
                  });
                }
                if (isObject && h2Item.key_messaging) {
                  sections.push(`**Messaging:** ${h2Item.key_messaging}`);
                }
                if (isObject && h2Item.cta) {
                  sections.push(`**CTA:** ${h2Item.cta}`);
                }
              });
            }

            if (page.primary_cta) {
              sections.push(`**CTA:** ${page.primary_cta}`);
            }
          }
        }

        sections.push('');
        sections.push('---');
      });
    }

    // Phase 5: Navigation Structure
    if (data.site_architecture.navigation_structure) {
      sections.push(formatHeading('Phase 5: Navigation Structure', 2));

      if (data.site_architecture.navigation_structure.primary_nav) {
        sections.push('**Primary Navigation:**');
        sections.push(data.site_architecture.navigation_structure.primary_nav.join(', '));
      }

      if (data.site_architecture.navigation_structure.secondary_nav) {
        sections.push('**Secondary Navigation:**');
        sections.push(data.site_architecture.navigation_structure.secondary_nav.join(', '));
      }

      if (data.site_architecture.navigation_structure.footer_nav) {
        sections.push('**Footer Navigation:**');
        sections.push(data.site_architecture.navigation_structure.footer_nav.join(', '));
      }
    }

    // Phase 6: Internal Linking Rules
    if (
      data.site_architecture.internal_linking_rules &&
      Object.keys(data.site_architecture.internal_linking_rules).length > 0
    ) {
      sections.push(formatHeading('Phase 6: Internal Linking Rules', 2));
      Object.entries(data.site_architecture.internal_linking_rules).forEach(
        ([pageType, rules]: [string, any]) => {
          sections.push(`**${pageType.replace('_', ' ')}:**`);
          sections.push(formatBulletList(rules));
        },
      );
    }

    // Sitemap
    if (data.site_architecture.sitemap) {
      sections.push(formatHeading('Sitemap', 2));
      sections.push('```');
      sections.push(data.site_architecture.sitemap);
      sections.push('```');
    }

    return sections.join('\n\n');
  }

  // Blog Post Formatting
  if (isBlogPost) {
    const sections: string[] = [];

    // SEO Metadata
    if (data.meta_title?.trim() || data.meta_description?.trim()) {
      sections.push(formatHeading('SEO Metadata', 2));
      if (data.meta_title?.trim()) {
        sections.push(`**Meta Title:** ${data.meta_title} (${Math.min(data.meta_title?.length || 0, 60)}/60)`);
      }
      if (data.meta_description?.trim()) {
        sections.push(
          `**Meta Description:** ${data.meta_description} (${
            Math.min(data.meta_description?.length || 0, 160)
          }/160)`,
        );
      }
    }

    // Article Title
    if (data.hero?.h1) {
      sections.push(formatHeading(data.hero.h1, 1));
    }

    // Article Content
    if (data.sections && data.sections.length > 0) {
      data.sections.forEach((section: any) => {
        if (section.heading) {
          sections.push(formatHeading(section.heading, 2));
        }

        if (section.subsections && section.subsections.length > 0) {
          section.subsections.forEach((subsection: any) => {
            if (subsection.subheading) {
              sections.push(formatHeading(subsection.subheading, 3));
            }
            if (subsection.content) {
              sections.push(subsection.content);
            }
          });
        }
      });
    }

    // FAQs
    if (data.faqs && data.faqs.length > 0) {
      sections.push(formatHeading('Frequently Asked Questions', 2));
      data.faqs.forEach((faq: any) => {
        sections.push(formatHeading(faq.question, 3));
        sections.push(faq.answer);
      });
    }

    // Final CTA
    if (data.cta_final) {
      sections.push(formatHeading('Call to Action', 2));
      sections.push(data.cta_final);
    }

    // SEO Suggestions
    if (data.internal_links && data.internal_links.length > 0) {
      sections.push(formatHeading('Internal Links', 2));
      sections.push(formatBulletList(data.internal_links));
    }

    if (data.image_alt_texts && data.image_alt_texts.length > 0) {
      sections.push(formatHeading('Image Alt Texts', 2));
      sections.push(formatBulletList(data.image_alt_texts));
    }

    return sections.join('\n\n');
  }

  // Landing Page Formatting
  if (isLandingPage) {
    const sections: string[] = [];

    // SEO Metadata
    if (data.meta_title?.trim() || data.meta_description?.trim()) {
      sections.push(formatHeading('SEO Metadata', 2));
      if (data.meta_title?.trim()) {
        sections.push(`**Meta Title:** ${data.meta_title} (${Math.min(data.meta_title?.length || 0, 60)}/60)`);
      }
      if (data.meta_description?.trim()) {
        sections.push(
          `**Meta Description:** ${data.meta_description} (${
            Math.min(data.meta_description?.length || 0, 160)
          }/160)`,
        );
      }
    }

    // SEO Keywords
    if (data.seo_keywords && data.seo_keywords.length > 0) {
      sections.push(formatHeading('SEO Keywords', 2));
      sections.push(data.seo_keywords.join(', '));
    }

    // Hero Section
    if (data.hero) {
      sections.push(formatHeading('Hero Section', 2));
      sections.push(formatHeading(data.hero.h1, 1));
      if (data.hero.h2) {
        sections.push(data.hero.h2);
      }
      if (data.hero.visual_placeholder) {
        sections.push(`* Visual: ${data.hero.visual_placeholder}*`);
      }
      if (data.hero.primary_cta) {
        sections.push(`**${data.hero.primary_cta}**`);
      }
      if (data.hero.secondary_cta) {
        sections.push(`${data.hero.secondary_cta}`);
      }
    }

    // CTA Variations
    if (data.cta_variations && data.cta_variations.length > 0) {
      sections.push(formatHeading('CTA Variations (A/B Testing)', 2));
      data.cta_variations.forEach((cta: string, index: number) => {
        sections.push(`${index + 1}. ${cta}`);
      });
    }

    // Main Content Sections
    if (data.sections && data.sections.length > 0) {
      data.sections.forEach((section: any) => {
        sections.push(formatHeading(section.heading, 2));

        if (section.subsections && section.subsections.length > 0) {
          section.subsections.forEach((subsection: any) => {
            if (subsection.subheading) {
              sections.push(formatHeading(subsection.subheading, 3));
            }
            if (subsection.content) {
              sections.push(subsection.content);
            }
          });
        }
      });
    }

    // Social Proof
    const hasSocialProofLanding =
      data.social_proof &&
      (data.social_proof.testimonials?.length > 0 ||
        data.social_proof.client_logos?.length > 0 ||
        data.social_proof.stats?.length > 0);

    if (hasSocialProofLanding) {
      sections.push(formatHeading('Social Proof', 2));

      if (data.social_proof.testimonials && data.social_proof.testimonials.length > 0) {
        sections.push(formatHeading('Testimonials', 3));
        data.social_proof.testimonials.forEach((testimonial: any) => {
          if (testimonial.quote) {
            sections.push(`> "${testimonial.quote}"`);
            const attribution = [testimonial.author, testimonial.role].filter(Boolean).join(', ');
            if (attribution) {
              sections.push(`> — **${attribution}**`);
            }
            sections.push('');
          }
        });
      }

      if (data.social_proof.client_logos && data.social_proof.client_logos.length > 0) {
        sections.push(formatHeading('Client Logos', 3));
        sections.push(data.social_proof.client_logos.filter(Boolean).join(' | '));
      }

      if (data.social_proof.stats && data.social_proof.stats.length > 0) {
        sections.push(formatHeading('Stats', 3));
        data.social_proof.stats.forEach((stat: any) => {
          if (stat.number && stat.label) {
            sections.push(`- **${stat.number}** — ${stat.label}`);
          }
        });
      }
    }

    // Credibility
    const hasCredibilityLanding =
      data.credibility &&
      (data.credibility.certifications?.length > 0 ||
        data.credibility.press_mentions?.length > 0 ||
        data.credibility.awards?.length > 0);

    if (hasCredibilityLanding) {
      sections.push(formatHeading('Credibility', 2));

      if (data.credibility.certifications && data.credibility.certifications.length > 0) {
        sections.push(formatHeading('Certifications', 3));
        sections.push(formatBulletList(data.credibility.certifications.filter(Boolean)));
      }

      if (data.credibility.press_mentions && data.credibility.press_mentions.length > 0) {
        sections.push(formatHeading('Press Mentions', 3));
        sections.push(formatBulletList(data.credibility.press_mentions.filter(Boolean)));
      }

      if (data.credibility.awards && data.credibility.awards.length > 0) {
        sections.push(formatHeading('Awards', 3));
        sections.push(formatBulletList(data.credibility.awards.filter(Boolean)));
      }
    }

    // Pricing
    if (data.pricing && data.pricing.length > 0) {
      sections.push(formatHeading('Pricing Plans', 2));

      data.pricing.forEach((plan: any) => {
        if (plan.plan_name) {
          sections.push(formatHeading(`${plan.plan_name}${plan.highlight ? ' ⭐' : ''}`, 3));
        }
        if (plan.price) {
          sections.push(`**${plan.price}**`);
          sections.push('');
        }
        if (plan.features && plan.features.length > 0) {
          sections.push(formatBulletList(plan.features.filter(Boolean)));
          sections.push('');
        }
        if (plan.cta) {
          sections.push(`**${plan.cta}**`);
        }
      });
    }

    // Asset Placeholders
    if (data.asset_placeholders && data.asset_placeholders.length > 0) {
      sections.push(formatHeading('Asset Placeholders', 2));
      data.asset_placeholders.forEach((asset: any) => {
        if (asset.type && asset.section) {
          sections.push(`**${asset.type.toUpperCase()} - ${asset.section}**`);
        }
        if (asset.description) {
          sections.push(asset.description);
        }
      });
    }

    // FAQs
    if (data.faqs && data.faqs.length > 0) {
      sections.push(formatHeading('Frequently Asked Questions', 2));
      data.faqs.forEach((faq: any) => {
        sections.push(formatHeading(faq.question, 3));
        sections.push(faq.answer);
      });
    }

    // Final CTA
    if (data.cta_final) {
      sections.push(formatHeading('Final Call to Action', 2));
      sections.push(data.cta_final);
    }

    // SEO Suggestions
    const hasSeoSuggestions =
      (data.internal_links && data.internal_links.length > 0) ||
      (data.image_alt_texts && data.image_alt_texts.length > 0);

    if (hasSeoSuggestions) {
      sections.push(formatHeading('SEO Suggestions', 2));

      if (data.internal_links && data.internal_links.length > 0) {
        sections.push(formatHeading('Internal Links', 3));
        sections.push(formatBulletList(data.internal_links));
      }

      if (data.image_alt_texts && data.image_alt_texts.length > 0) {
        sections.push(formatHeading('Image Alt Texts', 3));
        sections.push(formatBulletList(data.image_alt_texts));
      }
    }

    return sections.join('\n\n');
  }

  // Default/Fallback Landing Page Formatting
  const sections: string[] = [];

  // SEO Metadata
  if (data.meta_title?.trim() || data.meta_description?.trim()) {
    sections.push(formatHeading('SEO Metadata', 2));
    if (data.meta_title?.trim()) {
      const charCount = data.meta_title.length;
      sections.push(`**Meta Title:** ${data.meta_title} (${Math.min(charCount, 60)}/60 chars)`);
    }
    if (data.meta_description?.trim()) {
      const charCount = data.meta_description.length;
      sections.push(`**Meta Description:** ${data.meta_description} (${Math.min(charCount, 160)}/160 chars)`);
    }
  }

  // Hero Section
  if (data.hero) {
    sections.push(formatHeading('Hero Section', 2));
    if (data.hero.h1) {
      sections.push(formatHeading(data.hero.h1, 1));
    }
    if (data.hero.h2) {
      sections.push(data.hero.h2);
    }
    if (data.hero.visual_placeholder) {
      sections.push(`* Visual: ${data.hero.visual_placeholder}*`);
    }
    if (data.hero.primary_cta) {
      sections.push(`**${data.hero.primary_cta}**`);
    }
    if (data.hero.secondary_cta) {
      sections.push(`${data.hero.secondary_cta}`);
    }
    if (!data.hero.primary_cta && !data.hero.secondary_cta && data.hero.cta) {
      sections.push(`**${data.hero.cta}**`);
    }
  }

  // Main Content Sections
  if (data.sections && data.sections.length > 0) {
    data.sections.forEach((section: any) => {
      if (section.heading) {
        sections.push(formatHeading(section.heading, 2));
      }

      if (section.subsections && section.subsections.length > 0) {
        section.subsections.forEach((subsection: any) => {
          if (subsection.subheading) {
            sections.push(formatHeading(subsection.subheading, 3));
          }
          if (subsection.content) {
            sections.push(subsection.content);
          }
        });
      }
    });
  }

  // Social Proof
  const hasSocialProof =
    data.social_proof &&
    (data.social_proof.testimonials?.length > 0 ||
      data.social_proof.client_logos?.length > 0 ||
      data.social_proof.stats?.length > 0);

  if (hasSocialProof) {
    sections.push(formatHeading('Social Proof', 2));

    if (data.social_proof.testimonials && data.social_proof.testimonials.length > 0) {
      sections.push(formatHeading('Testimonials', 3));
      data.social_proof.testimonials.forEach((testimonial: any) => {
        if (testimonial.quote) {
          sections.push(`> "${testimonial.quote}"`);
          const attribution = [testimonial.author, testimonial.role].filter(Boolean).join(', ');
          if (attribution) {
            sections.push(`> — **${attribution}**`);
          }
          sections.push('');
        }
      });
    }

    if (data.social_proof.client_logos && data.social_proof.client_logos.length > 0) {
      sections.push(formatHeading('Client Logos', 3));
      sections.push(data.social_proof.client_logos.filter(Boolean).join(' | '));
    }

    if (data.social_proof.stats && data.social_proof.stats.length > 0) {
      sections.push(formatHeading('Stats', 3));
      data.social_proof.stats.forEach((stat: any) => {
        if (stat.number && stat.label) {
          sections.push(`- **${stat.number}** — ${stat.label}`);
        }
      });
    }
  }

  // Credibility
  const hasCredibility =
    data.credibility &&
    (data.credibility.certifications?.length > 0 ||
      data.credibility.press_mentions?.length > 0 ||
      data.credibility.awards?.length > 0);

  if (hasCredibility) {
    sections.push(formatHeading('Credibility', 2));

    if (data.credibility.certifications && data.credibility.certifications.length > 0) {
      sections.push(formatHeading('Certifications', 3));
      sections.push(formatBulletList(data.credibility.certifications.filter(Boolean)));
    }

    if (data.credibility.press_mentions && data.credibility.press_mentions.length > 0) {
      sections.push(formatHeading('Press Mentions', 3));
      sections.push(formatBulletList(data.credibility.press_mentions.filter(Boolean)));
    }

    if (data.credibility.awards && data.credibility.awards.length > 0) {
      sections.push(formatHeading('Awards', 3));
      sections.push(formatBulletList(data.credibility.awards.filter(Boolean)));
    }
  }

  // Pricing
  if (data.pricing && data.pricing.length > 0) {
    sections.push(formatHeading('Pricing Plans', 2));

    data.pricing.forEach((plan: any) => {
      if (plan.plan_name) {
        sections.push(formatHeading(`${plan.plan_name}${plan.highlight ? ' ⭐' : ''}`, 3));
      }
      if (plan.price) {
        sections.push(`**${plan.price}**`);
        sections.push('');
      }
      if (plan.features && plan.features.length > 0) {
        sections.push(formatBulletList(plan.features.filter(Boolean)));
        sections.push('');
      }
      if (plan.cta) {
        sections.push(`**${plan.cta}**`);
      }
    });
  }

  // Asset Placeholders
  if (data.asset_placeholders && data.asset_placeholders.length > 0) {
    sections.push(formatHeading('Asset Placeholders', 2));
    data.asset_placeholders.forEach((asset: any) => {
      if (asset.type && asset.section) {
        sections.push(`**${asset.type.toUpperCase()} - ${asset.section}**`);
      }
      if (asset.description) {
        sections.push(asset.description);
      }
    });
  }

  // FAQs
  if (data.faqs && data.faqs.length > 0) {
    sections.push(formatHeading('Frequently Asked Questions', 2));
    data.faqs.forEach((faq: any) => {
      if (faq.question) {
        sections.push(formatHeading(faq.question, 3));
      }
      if (faq.answer) {
        sections.push(faq.answer);
      }
    });
  }

  // Final CTA
  if (data.cta_final) {
    sections.push(formatHeading('Final Call to Action', 2));
    sections.push(data.cta_final);
  }

  // SEO Suggestions
  const hasSeoSuggestions =
    (data.internal_links && data.internal_links.length > 0) ||
    (data.image_alt_texts && data.image_alt_texts.length > 0);

  if (hasSeoSuggestions) {
    sections.push(formatHeading('SEO Suggestions', 2));

    if (data.internal_links && data.internal_links.length > 0) {
      sections.push(formatHeading('Internal Links', 3));
      sections.push(formatBulletList(data.internal_links.filter(Boolean)));
    }

    if (data.image_alt_texts && data.image_alt_texts.length > 0) {
      sections.push(formatHeading('Image Alt Texts', 3));
      sections.push(formatBulletList(data.image_alt_texts.filter(Boolean)));
    }
  }

  return sections.join('\n\n');
};
